import React, {useContext, useEffect, useState} from 'react';
import {Pressable, StyleSheet, View, Text, ScrollView} from 'react-native';
import {
    COLORS,
    COMMON_STYLES,
    Spinner,
    TEXT_STYLES,
    TYPOGRAPHY,
    VARIANTS_STYLES,
} from '@dehaat/dds';

import fetchFarmerOngoingCrops from '../apis/fetchFarmerOngoingCrops';
import {Crop, SowingRecord} from '../models/crop';

import {ResponsiveContext} from 'app/context/ResponsiveUIContext';
import fetchFarmerCrops from '../apis/fetchFarmerCrops';
import SelectCropModal from '../components/crop-calendar/SelectCropModal';
import AddSowingRecordModal from '../components/crop-calendar/AddSowingRecord';
import SowingRecordSuccessModal from '../components/crop-calendar/SowingRecordSuccessModal';
import CropCard from '../components/crop-calendar/CropCard';
import CropSelectionComponent from '../components/crops/AddCropModal';
import {OongoingCropsListingScreenProps, ROUTES} from '../routes/types';

interface AddSowingRecordCTAProps {
    onClick: () => void;
}

const AddSowingRecordCTA = ({onClick}: AddSowingRecordCTAProps) => {
    return (
        <Pressable onPress={onClick} style={styles.ctaContainer}>
            <View style={[styles.addCropBtn, styles.img84X84]}>
                <Text
                    style={[
                        TYPOGRAPHY.HEADING1,
                        VARIANTS_STYLES.EMPHASIZED,
                        TEXT_STYLES.textPrimary100,
                    ]}>
                    +
                </Text>
            </View>
            <Text
                style={[
                    TYPOGRAPHY.TEXT2,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textNeutral80,
                    COMMON_STYLES['mt1/2'],
                    COMMON_STYLES.textCenter,
                    styles.ctaText,
                ]}>
                Add ongoing crops
            </Text>
        </Pressable>
    );
};

const CropCalendar = ({navigation, route}: OongoingCropsListingScreenProps) => {
    const {mediaStyles} = useContext(ResponsiveContext);
    const {farmerId, isEditAllowed} = route.params;
    const [isModalVisible, setModalVisibility] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [farmerOngoingCrops, setFarmerOngoingCrops] = useState<
        SowingRecord[]
    >([]);
    const [showSowingRecordModal, setSowingRecordModal] = useState(false);
    const [showAddNewCropModal, setShowAddNewCropModal] = useState(false);

    const [successModalInfo, setShowSuccessModalInfo] = useState<{
        visible: boolean;
        record: SowingRecord | null;
    }>({
        visible: false,
        record: null,
    });

    const [selectedCrop, setSelectedCrop] = useState<Crop>();

    const [crops, setCrops] = useState<Crop[]>([]);

    const fetchAndSetFarmerCrops = async () => {
        setIsLoading(true);
        const results = await fetchFarmerCrops(farmerId);
        if (results) {
            setCrops(results || []);
        }
        setIsLoading(false);
    };

    const fetchAndUpdateSowingRecords = async () => {
        setIsLoading(true);
        const response = await fetchFarmerOngoingCrops(farmerId);
        if (response) {
            setFarmerOngoingCrops(response);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        if (isEditAllowed) fetchAndSetFarmerCrops();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditAllowed]);

    useEffect(() => {
        fetchAndUpdateSowingRecords().finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ScrollView contentContainerStyle={COMMON_STYLES.p1}>
            {isLoading ? (
                <Spinner type="on-screen" />
            ) : (
                <View
                    style={[
                        styles.gap1,
                        COMMON_STYLES.row,
                        mediaStyles({
                            mobileStyle: COMMON_STYLES.justifyCenter,
                        }),
                        COMMON_STYLES.flexWrap,
                    ]}>
                    {farmerOngoingCrops.map((sowingRecord, index) => (
                        <CropCard
                            key={index}
                            cropName={sowingRecord.crop.name}
                            cropImageUrl={sowingRecord.crop.image_url}
                            sowingDate={sowingRecord.sowing_date}
                            farmName={sowingRecord.farm_name}
                        />
                    ))}

                    {isEditAllowed && (
                        <AddSowingRecordCTA
                            onClick={() => setModalVisibility(true)}
                        />
                    )}
                </View>
            )}

            <SelectCropModal
                isVisible={isModalVisible}
                handleClose={() => setModalVisibility(false)}
                crops={crops}
                onCropSelect={selectedCrop => {
                    setSelectedCrop(selectedCrop);
                    setSowingRecordModal(true);
                    setModalVisibility(false);
                }}
                onAddNewCropClick={() => setShowAddNewCropModal(true)}
            />

            <AddSowingRecordModal
                isVisible={showSowingRecordModal}
                farmerId={farmerId}
                crop={selectedCrop!}
                onClose={() => setSowingRecordModal(false)}
                onSubmit={record => {
                    setSowingRecordModal(false);
                    setShowSuccessModalInfo({
                        visible: true,
                        record,
                    });
                }}
            />

            <SowingRecordSuccessModal
                isVisible={successModalInfo?.visible}
                handleClose={() => {
                    setShowSuccessModalInfo({visible: false, record: null});
                    fetchAndUpdateSowingRecords();
                }}
                record={successModalInfo.record!}
            />

            {showAddNewCropModal && (
                <CropSelectionComponent
                    farmerId={farmerId}
                    userCrops={crops}
                    onClose={() => setShowAddNewCropModal(false)}
                    onSubmit={() =>
                        navigation.replace(ROUTES.ONGOING_CROPS_LISTING, {
                            farmerId,
                            isEditAllowed,
                        })
                    }
                />
            )}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    ctaContainer: {width: 84, marginHorizontal: 40},
    img84X84: {
        height: 84,
        width: 84,
        borderRadius: 42,
    },
    addCropBtn: {
        borderWidth: 1,
        borderColor: COLORS.primary100,
        backgroundColor: COLORS.primary20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    ctaText: {
        letterSpacing: -0.25,
    },
    gap1: {
        gap: 16,
    },
});

export default CropCalendar;
